import * as React from 'react';
import { observer, inject } from 'mobx-react';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import DatePicker from 'react-date-picker';
import { Cart as ICart, OrderType } from '../models/interfaces';
import { DATE_FORMAT } from '../constants/constants';
import { OrderFormStore } from '../stores/OrderFormStore';
import Input from './Input';
import { NotificationStore } from '../stores/NotificationStore';
import * as moment from 'moment';

interface OrderFormFieldsProps {
  cart: ICart;
  orderFormStore: OrderFormStore;
  notificationStore: NotificationStore;
  orderTypes: Array<OrderType>;
  selectCustomerAutomatic: () => void;
}

interface OrderFormFieldsState {
  selectedOrderType: {
    value: string;
    label: string;
  };
  shipDate: Date;
  massOrder: boolean;
}

@observer
class OrderFormFields extends React.Component<OrderFormFieldsProps, OrderFormFieldsState> {

  constructor(props: OrderFormFieldsProps) {
    super(props);
    this.state = {
      selectedOrderType: { value: '', label: '' },
      shipDate: moment().add(2, 'days').toDate(),
      massOrder: false
    };

    this.props.orderFormStore.setMassOrder(this.state.massOrder);
  }

  componentDidMount() {
    if (this.props.orderFormStore.orderData.ShipDate) {
      let shipDate = moment(this.props.orderFormStore.orderData.ShipDate, DATE_FORMAT).toDate();
      this.shipDateChange(shipDate);
    } else {
      this.shipDateChange(moment().add(2, 'days').toDate());
    }
    if (this.props.orderFormStore.orderData.ReasonCode) {
      const orderType = this.findOrderType(this.props.orderFormStore.orderData.ReasonCode);
      if (orderType) {
        this.orderTypeChange({ value: orderType.ReasonCode, label: orderType.Description });
      }
    }
  }

  findOrderType(reasonCode: string) {
    return this.props.orderTypes.find(type => {
      return reasonCode === type.ReasonCode;
    });
  }

  orderTypeChange(selectedOrderType: { value: string, label: string }) {
    if (selectedOrderType) {
      this.setState({ selectedOrderType });
      this.props.orderFormStore.setOrderType(selectedOrderType.value);

      if (selectedOrderType.value === 'S1' || selectedOrderType.value === 'S2') {
        this.props.selectCustomerAutomatic();
      }
    } else {
      this.setState({ selectedOrderType: { value: '', label: '' } });
      this.props.orderFormStore.clearOrderType();
    }
  }

  shipDateChange(newShipDate: Date) {

    if (!newShipDate) {
      newShipDate = new Date();
    } else if (moment(newShipDate).isBefore(moment(), 'day')) {
      const error = {
        type: 'error',
        message: 'Toimituspäivä ei voi olla menneisyydessä',
        seen: false
      };

      this.props.notificationStore.addNotification(error);
      newShipDate = new Date();
    }

    this.setState({ shipDate: newShipDate });
    let dateString = moment(newShipDate).format(DATE_FORMAT);
    this.props.orderFormStore.setShipDate(dateString);
  }

  isCartEmpty() {
    return this.props.cart.products.length === 0 ? true : false;
  }

  render() {
    const {
      orderFormStore,
      orderTypes
    } = this.props;
    const maxDetailLenght = 250;

    const contactNameChange = (name: string) => orderFormStore.setContactName(name);
    const contactEmailChange = (email: string) => orderFormStore.setContactEmail(email);

    const massOrderChange = (value: string) => {
      const toggled: boolean = value === "true" ? true : false;
      this.setState({massOrder: toggled});
      orderFormStore.setMassOrder(toggled);
    }

    const orderDetailsChange = (details: string) => { 
      if (details.length > maxDetailLenght) {
        return;
      }
      orderFormStore.setOrderDetails(details);
    };
    const orderAccompanyingLetterChange = (value: string) => { 
      if (value.length > maxDetailLenght) {
        return;
      }
      orderFormStore.setAccompanyingLetter(value); 
    };

    const orderFormData = orderFormStore.orderData;
    const referenceLenghtClasses = (length: number) => {
      let classes = 'reference-length__current';
      if (length >= 250) {
        return `${classes} overlength`;
      }
      return classes;
    };

    return (
      <div className="">
        <div className={this.isCartEmpty() ? 'box box--tabbed box--disabled' : 'box box--tabbed'}>
          <div className="box__title"><h2>Tilauksen tekijän tiedot</h2></div>
          <div className="box__content box__content--flex">
            <div className="box__content__half">
              <div className="input-wrapper">
                <label htmlFor="order-name">Tilaajan nimi</label>
                <Input
                  id={'order-name'}
                  type={'text'}
                  required={true}
                  placeholder={'Nimi'}
                  value={orderFormData.ShipContact}
                  onChange={(value) => contactNameChange(value)}
                />
              </div>

              <div className="input-wrapper">
                <label htmlFor="order-email">Sähköposti</label>
                <Input
                  id="order-email"
                  type="email"
                  required={false}
                  placeholder="Sähköposti"
                  value={orderFormData.EMail}
                  onChange={(value) => contactEmailChange(value)}
                />
              </div>

              <div className="input-wrapper">
                <label htmlFor="order-date">Toimituspvm</label>
                <DatePicker
                  locale="fi"
                  onChange={(newDate) => this.shipDateChange(newDate)}
                  value={this.state.shipDate}
                />
              </div>
              <div className="input-wrapper">
                <label htmlFor="order-type">Ottotyyppi</label>
                <Select
                  name="order-type"
                  value={this.state.selectedOrderType.value}
                  placeholder="Valitse ottotyyppi"
                  noResultsText="Ei tuloksia"
                  onChange={(e: { value: string, label: string }) => this.orderTypeChange(e)}
                  options={orderTypes.map(orderType => {
                    return { value: orderType.ReasonCode, label: orderType.Description };
                  })}
                />
              </div>
              <div className="input-wrapper">
                <label htmlFor="mass-order">Massapostitus</label>
                <Input
                  id="1"
                  name="mass-order"
                  type="checkbox"
                  value={this.state.massOrder ? 1 : 0}
                  placeholder="Onko tilaus massatilaus"
                  onChange={(e) => massOrderChange(e)}
                />
              </div>
            </div>
            <div className="box__content__half">
              <div className="input-wrapper input-wrapper--column">
                <label htmlFor="order-info">Sisäinen info</label>
                <textarea
                  id="order-info"
                  placeholder="Kirjoita toiveesi..."
                  value={orderFormData.Reference}
                  onChange={(e) => orderDetailsChange(e.target.value)}
                />
                <div className="reference-length">
                  <span className={referenceLenghtClasses(orderFormData.Reference.length)}>
                    {orderFormData.Reference.length}
                  </span>
                  /
                  <span className="reference-length__max">250</span>
                </div>
              </div>
              <div className="input-wrapper input-wrapper--column">
                <label htmlFor="order-info">Saate asiakkaalle</label>
                <textarea
                  id="order-info"
                  placeholder="Kirjoita saate..."
                  value={orderFormData.YourReference2}
                  onChange={(e) => orderAccompanyingLetterChange(e.target.value)}
                />
                <div className="reference-length">
                  <span className={referenceLenghtClasses(orderFormData.YourReference2.length)}>
                    {orderFormData.YourReference2.length}
                  </span>
                  /
                  <span className="reference-length__max">250</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default OrderFormFields;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/OrderFormFields.tsx